import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EpgChannelVm, ProgramVm } from 'qp-ui-interface';
import { EpgData, EpgDateFilter } from '../../shared/models/epgData';
import { LocalStorageService } from '../../shared/services/local-storage/local-storage.service';
import { NavigationLibraryService } from '../../shared/services/navigation/navigation.service';
import { RoutingService } from '../../shared/services/routing/routing.service';
import { openBlockedContentMessageDialog } from '../../shared/shared-components/shared-dialogs';
import { generateTimeArray } from '../../shared/utils/epgUtils';
import { isLoggedIn } from '../../shared/utils/LoginUtils';
import { StorefrontService } from '../../storefront/storefront.service';
import { DataService } from '../../data.service';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { SubscriptionHandlerService } from '../../shared/services/subscription-handler.service';

@Component({
  selector: 'app-epg-grid',
  templateUrl: './epg-grid.component.html',
  styleUrls: ['./epg-grid.component.scss'],
})
export class EpgGridComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('filtersContainer') filtersContainer: ElementRef<HTMLElement>;
  @ViewChild('gridline') gridLine: ElementRef<HTMLElement>;
  @ViewChild('gridDot') gridDot: ElementRef<HTMLElement>;
  @ViewChild('epgContainer') epgContainer: ElementRef<HTMLElement>;
  @Input() playerMode = false;
  @Input() epgData: EpgData;
  @Output() goBack = new EventEmitter();

  // channels
  channels: EpgChannelVm[] = [];
  // selected program to show details
  selectedProgram: ProgramVm;
  //#region Component vars
  times: string[] = [];
  epgInterval: any;
  timePixelRatio: number = 14.3 / 30;
  startTimeEpoch = new Date().getTime();
  intervalMins = 30; // 30 mins
  selectedEpgProgram: ProgramVm;
  localFocusedElement: HTMLElement;
  isLoading = false;
  hasLoaded = false;
  showGridline = false;
  useLocalBackBehavior = true;
  //#endregion

  //#region pagination
  private pageSize = 1;
  private pageNumber = 1;
  private channelsCount = 0;
  private loadingFilter = false;
  //#endregion

  private componentName = 'EPGGridComponent';

  // filters
  epgFilters: EpgDateFilter[] = [];

  //#region intervals
  private destroy$ = new Subject<void>();
  //#endregion
  constructor(
    private readonly storefrontService: StorefrontService,
    private readonly navService: NavigationLibraryService,
    private readonly routingService: RoutingService,
    private readonly changeRef: ChangeDetectorRef,
    private readonly localStorage: LocalStorageService,
    private readonly dialog: MatDialog,
    private readonly dataService: DataService,
    private readonly subscriptionHandlerService: SubscriptionHandlerService
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.startTimer();
    const epgReload: Subscription = this.dataService.epgReload.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.navService.focusElement(this.localFocusedElement, 0, 0);
    });
    // Ensures singleton subscription
    this.subscriptionHandlerService.addSubscription(this.componentName, 'epgReload', epgReload);
  }

  ngOnDestroy(): void {
    this.subscriptionHandlerService.unsubscribeAllFromComponent(this.componentName);
    clearInterval(this.epgInterval);
    this.epgInterval = undefined;
    // Close subscribes
    this.destroy$.next();
    this.destroy$.complete();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.epgData && changes.epgData.currentValue) {
      if (!changes.epgData.previousValue) {
        this.setEpgData(changes.epgData.currentValue);
      }
    }
  }

  keyarrowLeft(): void {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && activeElement.classList.contains('grid-cell')) {
      const previousSibling = activeElement.previousElementSibling;
      if (!previousSibling || previousSibling.classList.contains('channel-column')) {
        this.navService.focusContainer('tv-menu', true);
      }
    }
  }

  /**
   * Navigate to home tab or close details
   */
  onBack(): void {
    if (this.playerMode) {
      this.closeDetails(true);
    } else if (!this.selectedProgram) {
      this.navService.focusContainer('tv-menu', true);
      this.useLocalBackBehavior = false;
    } else {
      this.closeDetails();
    }
  }

  /**
   * Center filters scroll on focus
   */
  centerFilterScroll(): void {
    this.useLocalBackBehavior = true;
    window.scrollTo(0, 0);
    // smooth scroll not supported by LG 2019
    // try {
    //   document.activeElement.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    // } catch {
    const container = this.filtersContainer.nativeElement;
    const element = document.activeElement as HTMLElement;
    if (container && element) {
      const newScrollLeft = element.offsetLeft + element.offsetWidth - container.clientWidth;
      container.scrollLeft = newScrollLeft;
    }
    // }
  }

  /**
   * Select the channel's program active and update the scrolls
   *
   * @param channelId
   */
  moveToChannel(channelId: string): void {
    const channel = this.channels.find((c) => c.id === channelId);
    if (channel) {
      const activeIndex = channel.programs.findIndex((el) => el.active);
      if (activeIndex >= 0) {
        this.selectedEpgProgram = channel.programs[activeIndex];
      } else {
        this.selectedEpgProgram = channel.programs[0];
      }
      const channelRow = document.querySelector(`[data-channelid="${channelId}"]`)?.parentElement;
      const programs = channelRow?.getElementsByClassName('grid-cell') as HTMLCollectionOf<HTMLElement>;
      let activeProgram = programs?.item(0);
      if (programs) {
        for (const pr of programs) {
          if (pr.classList.contains('active')) {
            activeProgram = pr;
            break;
          }
        }
      }
      activeProgram?.focus();
    }
  }

  /**
   * select program from grid and show details setting the selectedProgram value
   * If the epg was load from the player and the program is active, emit the onBack to close the epg dialog
   *
   * @param program
   */
  async selectProgram(program: ProgramVm) {
    if (program.active) {
      await this.routingService.navigateDirectoToPlayerFromChannelWithValidations(program, '/epg');
    } else {
      // show the details side dialog
      this.selectedProgram = program;
      this.saveLocalFocus();
    }
  }

  /**
   * Close program details and restore the focus
   *
   */
  closeDetails(emitGoBack = false): void {
    if (emitGoBack) {
      // Closes the player EPG
      this.goBack.emit();
    } else if (this.channels.length > 0) {
      // Restores the focus to the correct program
      this.restoreLocalFocus();
    } else {
      // Focuses the dates
      this.navService.focusContainer('filters', true);
    }
    this.selectedProgram = undefined;
  }

  /**
   * Save the local focus before open the program details component
   */
  saveLocalFocus(): void {
    if (document.activeElement.classList.contains('grid-cell')) {
      this.localFocusedElement = document.activeElement as HTMLElement;
    }
  }

  /**
   * Restore local focus after close the program details component
   */
  restoreLocalFocus(): void {
    if (this.localFocusedElement) {
      this.localFocusedElement?.focus();
    } else if (this.channels.length > 0) {
      this.moveToChannel(this.channels[0].id);
    }
  }

  /**
   * Close details and play content
   * Only the past-future programs can open the details and trigger this function
   *
   * @param program
   * @returns
   */
  async playContent(program: ProgramVm): Promise<void> {
    if (Date.now() > program.endTimeEpoch) {
      // Redirect to the player with catch up
      const redirected = await this.routingService.navigateDirectoToPlayerFromCatchupWithValidations(program, '/epg');
      if (redirected) {
        this.closeDetails(this.playerMode); // If in player mode it will close the player EPG
      }
    } else {
      // Redirect to the player with the channel
      const redirected = await this.routingService.navigateDirectoToPlayerFromChannelWithValidations(program, '/epg');
      if (redirected) {
        this.closeDetails(this.playerMode); // If in player mode it will close the player EPG
      }
    }
  }

  /**
   * Redirect to login
   * close details
   */
  navigateToLogin() {
    this.closeDetails();
    this.routingService.navigateToLogin('/epg');
  }

  /**
   * show subscription message
   * do not close the details unless is redirecting to login
   */
  showSubscriptionMessage() {
    const isLogged = isLoggedIn(this.localStorage);
    openBlockedContentMessageDialog(this.dialog, isLogged).then((value) => {
      if (value) {
        if (!isLogged) {
          this.navigateToLogin();
        }
      }
    });
  }

  /**
   * Update the scroll values to center the row vertically
   */
  centerRow() {
    try {
      const col = document.activeElement as HTMLElement;
      const div = this.epgContainer.nativeElement as HTMLElement;
      if (col != null && div != null) {
        //remove horizontal 2px of focus
        //remove 8.4vh (19.2 to convert px) of times header
        // remove left channel space
        this.epgContainer.nativeElement.scrollTo({ behavior: 'smooth', left: col.offsetLeft - 145, top: col.offsetTop - 8.4 * 19.2 });
      }
    } catch {
      const col = document.activeElement as HTMLElement;
      const targetX = col.offsetLeft - 2;
      const targetY = col.offsetTop - 8.4 * 19.2;

      // Set the scroll position directly
      this.epgContainer.nativeElement.scrollLeft = targetX;
      this.epgContainer.nativeElement.scrollTop = targetY;
    }
    this.saveLocalFocus();
  }

  /**
   * Save all the channels in the all tab (after pagination)
   * select filter and do the call by filter and show the result in the grid
   * update filter
   *
   * @param filterId
   */
  async applyFilter(filter: EpgDateFilter): Promise<void> {
    const activeFilter = this.epgFilters.find((e) => e.active);
    if (filter.date === activeFilter?.date) return;
    if (this.loadingFilter) return;
    this.epgFilters.forEach((f) => (f.active = f.date === filter.date));
    this.isLoading = true;
    this.showGridline = false;
    try {
      this.channels = [];
      const response = await this.storefrontService.fetchEpgData({ pageNumber: 1, pageSize: this.pageSize }, filter.date.toISOString());
      await this.setEpgData(response);
    } catch {}
    this.isLoading = false;
    this.loadingFilter = false;
    this.showGridline = filter.isToday;
  }

  /**
   * Manage pagination on program focus
   *
   * @param rowIndex
   * @returns
   */
  async managePagination(rowIndex: number) {
    if (!this.useLocalBackBehavior) this.useLocalBackBehavior = true;
    const tab = this.epgFilters.find((f) => f.active);
    if (tab && !this.isLoading) {
      if (this.pageNumber * this.pageSize < this.channelsCount) {
        if (this.channels.length - rowIndex <= (this.playerMode ? 3 : 2)) {
          // value depends on the epg grid height
          //get the next page
          this.isLoading = true;
          this.pageNumber += 1;
          this.centerRow();
          try {
            const { channels, channelsCount } = await this.storefrontService.fetchEpgPage(
              this.pageNumber,
              this.pageSize,
              tab.date.toISOString()
            );
            this.channelsCount = channelsCount;
            this.channels.push(...channels);
            this.isLoading = false;
          } catch {
            this.isLoading = false;
          }
        }
      }
    }
    this.centerRow();
  }

  /**
   * update epg line every 1 minute
   */
  private startTimer() {
    this.updateEpgLine(new Date().getTime());
    this.epgInterval = setInterval(() => {
      const currentTimeEpoch = new Date().getTime();
      this.updateEpgLine(currentTimeEpoch);
      this.updateEpg(currentTimeEpoch);
    }, 60000);
  }

  /**
   * Calculate line position based on day's minutes
   */
  private updateEpgLine(currentTimeEpoch: number) {
    const time = currentTimeEpoch - this.startTimeEpoch;
    const linePosition = (time / 60000) * this.timePixelRatio;
    this.showGridline = !!this.gridLine?.nativeElement && !!this.gridDot?.nativeElement;
    this.changeRef.detectChanges();
    if (this.gridLine?.nativeElement) this.gridLine.nativeElement.style.left = `${linePosition}vw`;
    if (this.gridDot?.nativeElement) this.gridDot.nativeElement.style.left = `${linePosition - 0.6}vw`;
  }

  /**
   * Update the program active state based on the time
   *
   * @param currentTimeEpoch
   */
  private updateEpg(currentTimeEpoch: number) {
    this.channels = this.channels?.map((channel) => {
      channel.programs?.map((program: ProgramVm) => {
        program.active = program.startTimeEpoch <= currentTimeEpoch && currentTimeEpoch < program.endTimeEpoch;
        return program;
      });
      return channel;
    });
  }

  /**
   * Search for the channel's active program
   *
   * @param channel
   */
  setActiveProgram(channel: EpgChannelVm) {
    if (channel) {
      setTimeout(() => {
        this.updateEpgLine(new Date().getTime());
        this.moveToChannel(channel.id);
      }, 500);
    }
  }

  /**
   * Setup all the epg values based on the API response
   *
   * @param epgData
   * @returns
   */
  private async setEpgData(epgData: EpgData) {
    if (!epgData) return;
    const { startDate, endDate, dates, channels, channelsCount, pageNumber, pageSize } = epgData;
    this.times = generateTimeArray(startDate, endDate, this.intervalMins);
    this.startTimeEpoch = new Date(startDate).getTime();
    this.pageNumber = pageNumber;
    this.pageSize = pageSize;
    this.channelsCount = channelsCount;
    this.channels = channels;
    this.epgFilters = dates ?? [];
    this.isLoading = false;
    this.hasLoaded = true;
    this.setActiveProgram(this.channels[0]);
  }
}
